import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Sparkles, Link2, Loader2, ArrowRight, ArrowLeft, Wand2, Download, RefreshCw,
  CheckCircle2, AlertCircle, Upload, X, Pencil, Save, Trash2, Image as ImageIcon,
  Home, ShieldCheck, FolderOpen, Crown, Rocket, Layers, Film, Megaphone,
  AlertTriangle, TrendingUp, Repeat, Eye, ChevronLeft, ChevronRight,
} from "lucide-react";
import { useAuth, apiClient, formatApiError } from "../../lib/auth";
import GatedPage, { usePaywall } from "./GatedPage";
import WelcomeBanner from "../../components/WelcomeBanner";
import { renderAd, AD_SIZES } from "../../lib/adRenderer";
import { toast } from "sonner";

export default function CreativeMaker() {
  return (
    <GatedPage featureName="Creative Maker">
      <CreativeStudio />
    </GatedPage>
  );
}

// Budget-based cap on actively-promoting listings (matches the Lead Engine).
function listingCap(budget) {
  if (budget >= 2000) return 3;
  if (budget >= 1000) return 2;
  return 1;
}

// The four creatives that make up a Listing Campaign Kit.
const ASSET_DEFS = {
  hero:      { label: "Hero Image Ad",   sub: "Square · Meta & Instagram feed", template: "Premium Listing",     size: "1080x1080", Icon: ImageIcon },
  lifestyle: { label: "Lifestyle Ad",    sub: "Portrait · scroll-stopping",     template: "Modern Minimal",      size: "1080x1350", Icon: Sparkles },
  carousel:  { label: "Carousel",        sub: "Multi-photo · swipeable",        template: "Property Highlights", size: "1080x1080", Icon: Layers },
  reel:      { label: "Reel / Story",    sub: "Vertical · Reels & Stories",     template: "Modern Minimal",      size: "1080x1920", Icon: Film },
};
const ASSET_ORDER = ["hero", "lifestyle", "carousel", "reel"];

// Soft limit for headlines so they fit cleanly (2 lines) in the creatives.
const HEADLINE_MAX = 48;
const PRIMARY_MAX = 150; // Meta truncates primary text with "…See more" around here
const CTA_MAX = 20;      // CTA pill stays compact

function fileToDataUrl(file, maxDim = 1600) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const longSide = Math.max(width, height);
        let scale = 1;
        if (longSide > maxDim) scale = maxDim / longSide;        // downscale large
        else if (longSide < 1200) scale = Math.min(1200 / longSide, 2); // gently upscale small
        width = Math.max(1, Math.round(width * scale));
        height = Math.max(1, Math.round(height * scale));
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        const cx = c.getContext("2d");
        cx.imageSmoothingEnabled = true;
        cx.imageSmoothingQuality = "high";
        cx.drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", 0.9));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function pngToJpeg(pngDataUrl, quality = 0.82) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.width; c.height = img.height;
      c.getContext("2d").drawImage(img, 0, 0);
      resolve(c.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve(pngDataUrl);
    img.src = pngDataUrl;
  });
}

const EMPTY_PROP = {
  address: "", city: "", price: "", beds: "", baths: "", sqft: "",
  property_type: "", status: "", description: "", features: [],
};

const emptyKit = () => ({
  headlines: [], primaryTexts: [], cta: "Book a Tour", seller: null,
  assets: {
    hero: { photoIndex: 0, headlineIndex: 0 },
    lifestyle: { photoIndex: 0, headlineIndex: 1 },
    carousel: { photoIndices: [0], headlineIndex: 0 },
    reel: { photoIndex: 0, headlineIndex: 2 },
  },
});

function CreativeStudio() {
  const { user, updateUser } = useAuth();
  const { locked, showUpgrade } = usePaywall();
  const navigate = useNavigate();
  const location = useLocation();
  const isElite = user?.plan === "elite";

  const [stage, setStage] = useState("input"); // input | working | verify | results
  const [url, setUrl] = useState("");
  const [importError, setImportError] = useState(null);
  const [workingStep, setWorkingStep] = useState(0);

  const [property, setProperty] = useState(EMPTY_PROP);
  const [photos, setPhotos] = useState([]);
  const [selected, setSelected] = useState([]);
  const [rights, setRights] = useState(false);
  const [genError, setGenError] = useState(null);

  const [kit, setKit] = useState(emptyKit());
  const [sellerEnabled, setSellerEnabled] = useState(false);
  const [propertyId, setPropertyId] = useState(() => `prop_${Math.random().toString(36).slice(2, 10)}`);

  const [limitModal, setLimitModal] = useState(false);
  const [savedPromoteId, setSavedPromoteId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [library, setLibrary] = useState([]);
  const fileRef = useRef(null);

  const branding = {
    name: user?.name || "",
    brokerage: user?.brokerage_name || user?.agency || "",
    phone: user?.business_phone || user?.phone || "",
    email: user?.business_email || "",
    logo: user?.brand_logo || null,
    headshot: user?.headshot || user?.avatar || null,
    primary: user?.brand_color_primary || "#1D6AFF",
    accent: user?.brand_color_accent || "#F59E0B",
  };

  const engine = user?.engine || {};
  const cap = listingCap(engine.budget || 0);
  const activeIds = engine.activeListings || [];

  const loadLibrary = async () => {
    try {
      const { data } = await apiClient.get("/creatives/library");
      setLibrary(data.items || []);
    } catch (_e) { /* ignore */ }
  };
  useEffect(() => { if (!locked) loadLibrary(); /* eslint-disable-next-line */ }, [locked]);

  useEffect(() => {
    const st = location.state;
    if (!st) return;
    if (st.importUrl) setUrl(st.importUrl);
    if (st.manual && !st.importUrl) startManual();
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runWorkingAnimation = () => {
    setWorkingStep(0);
    [900, 1700, 2600].forEach((t, i) => setTimeout(() => setWorkingStep(i + 1), t));
  };

  const importListing = async () => {
    if (locked) { showUpgrade("AI ad generation"); return; }
    if (!/^https?:\/\//i.test(url.trim())) { setImportError("Paste a full listing link starting with http(s)://"); return; }
    setImportError(null);
    setStage("working");
    runWorkingAnimation();
    try {
      const { data } = await apiClient.post("/creatives/import-listing", { url: url.trim() });
      setProperty({ ...EMPTY_PROP, ...data.property });
      setPhotos(data.photos || []);
      setSelected((data.photos || []).map((_, i) => i).slice(0, 5));
      setRights(false);
      setStage("verify");
    } catch (e) {
      const status = e?.response?.status;
      const msg = formatApiError(e, "Import failed.");
      if (status === 409) { toast.error("Already imported", { description: msg }); startManual(); return; }
      setImportError(msg);
      setStage("input");
      toast.error("Automatic import didn't work", { description: "Enter the details manually below." });
    }
  };

  const onManualPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const loaded = [];
    for (const f of files.slice(0, 10 - photos.length)) {
      try { loaded.push(await fileToDataUrl(f)); } catch (_e) { /* skip */ }
    }
    setPhotos((prev) => {
      const next = [...prev, ...loaded];
      setSelected(next.map((_, i) => i).slice(0, 5));
      return next;
    });
    if (fileRef.current) fileRef.current.value = "";
  };

  const startManual = () => {
    setProperty(EMPTY_PROP); setPhotos([]); setSelected([]); setRights(false); setImportError(null);
    setStage("verify");
  };

  const setField = (k, v) => setProperty((p) => ({ ...p, [k]: v }));
  const toggleSelected = (i) =>
    setSelected((s) => {
      if (s.includes(i)) return s.filter((x) => x !== i);
      if (s.length >= 6) { toast.info("Up to 6 photos", { description: "Deselect one to choose another." }); return s; }
      return [...s, i];
    });
  const removePhoto = (i) => {
    setPhotos((prev) => prev.filter((_, idx) => idx !== i));
    setSelected((prev) => prev.filter((x) => x !== i).map((x) => (x > i ? x - 1 : x)));
  };

  // ---- Generate the Listing Campaign Kit ------------------------------
  const generateKit = async () => {
    if (locked) { showUpgrade("AI ad generation"); return; }
    if (!rights) { setGenError("Please confirm you have the rights to advertise this listing and use its photos."); return; }
    const keptPhotos = selected.map((i) => photos[i]).filter(Boolean);
    if (!keptPhotos.length) { setGenError("Select at least one photo (or upload one)."); return; }
    setGenError(null);
    setStage("working");
    runWorkingAnimation();
    try {
      const { data } = await apiClient.post("/creatives/generate-kit", {
        property, photos: keptPhotos, seller_angle: sellerEnabled && isElite,
        agent: { name: branding.name, brokerage: branding.brokerage, phone: branding.phone },
      });
      const ph = data.photos || keptPhotos;
      setPhotos(ph);
      setSelected(ph.map((_, i) => i));
      const hero = Math.min(data.hero_index || 0, ph.length - 1);
      const hl = data.headlines || [];
      const car = (data.carousel_order && data.carousel_order.length)
        ? data.carousel_order.filter((i) => i >= 0 && i < ph.length)
        : ph.map((_, i) => i).slice(0, Math.min(5, ph.length));
      const hi = (n) => (hl.length ? n % hl.length : 0);
      setKit({
        headlines: hl,
        primaryTexts: data.primary_texts || [],
        cta: data.cta || "Book a Tour",
        seller: data.seller || null,
        assets: {
          hero: { photoIndex: hero, headlineIndex: hi(0) },
          lifestyle: { photoIndex: Math.min(hero + 1, ph.length - 1), headlineIndex: hi(1) },
          carousel: { photoIndices: car.length ? car : [hero], headlineIndex: hi(0) },
          reel: { photoIndex: hero, headlineIndex: hi(2) },
        },
      });
      setStage("results");
    } catch (e) {
      setGenError(formatApiError(e, "Campaign generation failed."));
      setStage("verify");
      toast.error("Couldn't build your campaign", { description: formatApiError(e, "Try again.") });
    }
  };

  // ---- Kit mutators ---------------------------------------------------
  const setAsset = (key, patch) =>
    setKit((k) => ({ ...k, assets: { ...k.assets, [key]: { ...k.assets[key], ...patch } } }));

  const setHeroPhoto = (idx) =>
    setKit((k) => ({
      ...k,
      assets: {
        ...k.assets,
        hero: { ...k.assets.hero, photoIndex: idx },
        reel: { ...k.assets.reel, photoIndex: idx },
      },
    }));

  const regenerateAsset = (key) =>
    setKit((k) => {
      const a = k.assets[key];
      const nextH = k.headlines.length ? ((a.headlineIndex + 1) % k.headlines.length) : 0;
      if (key === "carousel") {
        // rotate the lead carousel photo
        const inds = a.photoIndices.length ? [...a.photoIndices.slice(1), a.photoIndices[0]] : [0];
        return { ...k, assets: { ...k.assets, carousel: { photoIndices: inds, headlineIndex: nextH } } };
      }
      const nextP = photos.length ? ((a.photoIndex + 1) % photos.length) : 0;
      return { ...k, assets: { ...k.assets, [key]: { photoIndex: nextP, headlineIndex: nextH } } };
    });

  const toggleCarouselPhoto = (idx) =>
    setKit((k) => {
      const cur = k.assets.carousel.photoIndices;
      let next;
      if (cur.includes(idx)) {
        if (cur.length <= 2) { toast.info("Keep at least 2 photos for a carousel"); return k; }
        next = cur.filter((x) => x !== idx);
      } else {
        if (cur.length >= 6) { toast.info("Up to 6 carousel photos"); return k; }
        next = [...cur, idx];
      }
      return { ...k, assets: { ...k.assets, carousel: { ...k.assets.carousel, photoIndices: next } } };
    });

  const updateHeadline = (i, v) => setKit((k) => ({ ...k, headlines: k.headlines.map((h, idx) => (idx === i ? v : h)) }));
  const updatePrimary = (i, v) => setKit((k) => ({ ...k, primaryTexts: k.primaryTexts.map((h, idx) => (idx === i ? v : h)) }));
  const updateCta = (v) => setKit((k) => ({ ...k, cta: v }));

  // ---- Render helpers -------------------------------------------------
  const buildAssetOpts = (key, photoOverride) => {
    const a = kit.assets[key];
    const def = ASSET_DEFS[key];
    const hIdx = Math.min(a.headlineIndex || 0, Math.max(0, kit.headlines.length - 1));
    const pIdx = photoOverride != null ? photoOverride : (key === "carousel" ? (a.photoIndices[0] ?? 0) : (a.photoIndex ?? 0));
    return {
      template: def.template,
      size: def.size,
      photo: photos[pIdx] || photos[0],
      copy: {
        headline: kit.headlines[hIdx] || property.address || property.city || "New Listing",
        subhead: (kit.primaryTexts[0] || "").slice(0, 70),
        cta: kit.cta || "Learn More",
        highlights: (property.features || []).slice(0, 3),
      },
      property, branding, priceVisible: true,
    };
  };

  const assetDepKey = (key) =>
    `${key}|${JSON.stringify(kit.assets[key])}|${kit.headlines.join("~")}|${kit.cta}|${photos.length}`;

  const downloadAsset = async (key) => {
    try {
      const dataUrl = await renderAd(buildAssetOpts(key));
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `newground-${(property.address || "listing").slice(0, 24).replace(/\W+/g, "-")}-${key}.png`;
      a.click();
    } catch (_e) { toast.error("Download failed"); }
  };

  // ---- Save / Promote -------------------------------------------------
  const buildKitPayload = async (heroPng) => ({
    name: property.address || property.city || "Listing",
    template: "Campaign Kit",
    size: "1080x1080",
    audience: "buyer",
    property_id: propertyId,
    property,
    copy: {
      headlines: kit.headlines,
      primary_texts: kit.primaryTexts,
      cta: kit.cta,
      seller: sellerEnabled && isElite ? kit.seller : null,
      assets: kit.assets,
      kind: "campaign_kit",
    },
    photo: photos[kit.assets.hero.photoIndex] || photos[0],
    photos,
    png: heroPng,
  });

  const saveKit = async () => {
    try {
      const heroPng = await renderAd(buildAssetOpts("hero"));
      await apiClient.post("/creatives/library", await buildKitPayload(heroPng));
      toast.success("Saved to your library", { description: "Your campaign is marked Ready." });
      loadLibrary();
    } catch (e) { toast.error("Save failed", { description: formatApiError(e) }); }
  };

  const promoteWithLeadEngine = async () => {
    if (locked) { showUpgrade("publishing to your Lead Engine"); return; }
    let id;
    try {
      const heroPng = await renderAd(buildAssetOpts("hero"));
      const { data } = await apiClient.post("/creatives/library", await buildKitPayload(heroPng));
      id = data.id;
      loadLibrary();
    } catch (e) { toast.error("Couldn't save campaign", { description: formatApiError(e) }); return; }

    if (activeIds.length >= cap) {
      setSavedPromoteId(id);
      setLimitModal(true);
      return;
    }
    try {
      await updateUser({ engine: { ...engine, activeListings: [...activeIds, id] } });
      toast.success("Added to your Lead Engine", {
        description: sellerEnabled && isElite
          ? "Newground will auto-balance budget across buyer leads, seller leads and this listing."
          : "Newground will automatically allocate budget to this listing alongside your leads.",
      });
      navigate("/dashboard/leads");
    } catch (e) { toast.error("Couldn't activate listing", { description: formatApiError(e) }); }
  };

  // ---- Library ---------------------------------------------------------
  const reopenLibrary = async (idv) => {
    try {
      const { data } = await apiClient.get(`/creatives/library/${idv}`);
      const it = data.item || {};
      const srcPhotos = (it.photos && it.photos.length) ? it.photos : (it.photo ? [it.photo] : []);
      if (!srcPhotos.length) {
        toast.error("Can't re-edit this one", { description: "Download it or promote it as-is." });
        return;
      }
      const cp = it.copy || {};
      const headlines = (cp.headlines && cp.headlines.length) ? cp.headlines : (cp.headline ? [cp.headline] : ["New Listing"]);
      const primaryTexts = (cp.primary_texts && cp.primary_texts.length) ? cp.primary_texts : (cp.primary_text ? [cp.primary_text] : [""]);
      setProperty({ ...EMPTY_PROP, ...(it.property || {}) });
      setPhotos(srcPhotos);
      setSelected(srcPhotos.map((_, i) => i));
      setPropertyId(it.property_id || `prop_${Math.random().toString(36).slice(2, 10)}`);
      setSellerEnabled(!!cp.seller);
      const hi = (n) => (headlines.length ? n % headlines.length : 0);
      const car = (cp.assets?.carousel?.photoIndices || srcPhotos.map((_, i) => i).slice(0, Math.min(5, srcPhotos.length)));
      setKit({
        headlines, primaryTexts, cta: cp.cta || "Learn More", seller: cp.seller || null,
        assets: {
          hero: cp.assets?.hero || { photoIndex: 0, headlineIndex: hi(0) },
          lifestyle: cp.assets?.lifestyle || { photoIndex: Math.min(1, srcPhotos.length - 1), headlineIndex: hi(1) },
          carousel: { photoIndices: car, headlineIndex: cp.assets?.carousel?.headlineIndex ?? hi(0) },
          reel: cp.assets?.reel || { photoIndex: 0, headlineIndex: hi(2) },
        },
      });
      setStage("results");
    } catch (e) { toast.error("Couldn't reopen", { description: formatApiError(e) }); }
  };

  const useLibraryInLeadGen = async (idv) => {
    if (locked) { showUpgrade("publishing to your Lead Engine"); return; }
    if (activeIds.includes(idv)) { toast.info("Already an active listing"); navigate("/dashboard/leads"); return; }
    if (activeIds.length >= cap) {
      navigate("/dashboard/leads", { state: { activateListingId: idv } });
      return;
    }
    try {
      await updateUser({ engine: { ...engine, activeListings: [...activeIds, idv] } });
      toast.success("Added to your Lead Engine");
      navigate("/dashboard/leads");
    } catch (e) { toast.error("Couldn't activate listing", { description: formatApiError(e) }); }
  };

  const deleteLib = async (idv) => { await apiClient.delete(`/creatives/library/${idv}`); loadLibrary(); };
  const downloadLib = async (idv) => {
    try {
      const { data } = await apiClient.get(`/creatives/library/${idv}`);
      const a = document.createElement("a");
      a.href = data.item.png; a.download = `${(data.item.name || "creative").replace(/\W+/g, "-")}.png`; a.click();
    } catch (_e) { toast.error("Couldn't load creative"); }
  };

  const reset = () => {
    setStage("input"); setUrl(""); setImportError(null);
    setProperty(EMPTY_PROP); setPhotos([]); setSelected([]); setRights(false);
    setKit(emptyKit()); setGenError(null); setSellerEnabled(false);
    setPropertyId(`prop_${Math.random().toString(36).slice(2, 10)}`);
  };

  return (
    <div data-testid="creative-maker">
      <WelcomeBanner planOverride="pro" subtitle={null} />

      {stage === "input" && (
        <InputStage
          url={url} setUrl={setUrl} onImport={importListing}
          importError={importError} onManual={startManual} library={library}
          activeIds={activeIds}
          onReopen={reopenLibrary} onUseLib={useLibraryInLeadGen}
          onDeleteLib={deleteLib} onDownloadLib={downloadLib}
        />
      )}

      {stage === "working" && <WorkingStage step={workingStep} />}

      {stage === "verify" && (
        <VerifyStage
          property={property} setField={setField}
          photos={photos} selected={selected} toggleSelected={toggleSelected} removePhoto={removePhoto}
          onUploadClick={() => fileRef.current?.click()}
          rights={rights} setRights={setRights}
          genError={genError} onBack={reset} onGenerate={generateKit}
        />
      )}

      {stage === "results" && (
        <CampaignKitStage
          kit={kit} photos={photos} property={property}
          buildAssetOpts={buildAssetOpts} assetDepKey={assetDepKey}
          onSwapHero={setHeroPhoto} onSetAsset={setAsset} onRegenerate={regenerateAsset}
          onToggleCarouselPhoto={toggleCarouselPhoto}
          updateHeadline={updateHeadline} updatePrimary={updatePrimary} updateCta={updateCta}
          onDownloadAsset={downloadAsset}
          isElite={isElite} sellerEnabled={sellerEnabled} setSellerEnabled={setSellerEnabled}
          onBack={() => setStage("verify")} onReset={reset}
          onPreview={() => setPreviewOpen(true)} onPromote={promoteWithLeadEngine} onSave={saveKit}
        />
      )}

      {previewOpen && (
        <PreviewModal
          kit={kit} photos={photos} buildAssetOpts={buildAssetOpts}
          isElite={isElite} sellerEnabled={sellerEnabled}
          onClose={() => setPreviewOpen(false)}
        />
      )}

      {limitModal && (
        <LimitModal
          cap={cap}
          onClose={() => setLimitModal(false)}
          onReplace={() => { setLimitModal(false); navigate("/dashboard/leads", { state: { activateListingId: savedPromoteId } }); }}
          onIncrease={() => { setLimitModal(false); navigate("/dashboard/leads", { state: { focusBudget: true } }); }}
        />
      )}

      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onManualPhotos} />
    </div>
  );
}

// ─────────────────────────── INPUT STAGE ─────────────────────────────────
function InputStage({ url, setUrl, onImport, importError, onManual, library, activeIds, onDeleteLib, onDownloadLib, onReopen, onUseLib }) {
  return (
    <div className="space-y-8">
      <div className="relative overflow-hidden bg-white border border-slate-200 rounded-3xl px-6 sm:px-12 py-12 sm:py-16 shadow-[0_30px_80px_-40px_rgba(15,23,42,0.25)] text-center">
        <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#1D6AFF]/8 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-amber-200/30 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#1D6AFF] bg-[#1D6AFF]/8 border border-[#1D6AFF]/20 rounded-full px-3 py-1 mb-6">
            <Sparkles className="w-3 h-3" /> AI Ad Studio
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-3 leading-tight">
            Turn your listing into an ad.
          </h1>
          <p className="text-base sm:text-lg text-slate-500 mb-8">
            Paste your property link. Newground builds a full campaign kit.
          </p>

          <div className="flex flex-col sm:flex-row items-stretch gap-3 max-w-xl mx-auto">
            <div className="flex-1 flex items-center bg-slate-50 border border-slate-200 rounded-2xl px-4 focus-within:border-[#1D6AFF] focus-within:bg-white transition-all">
              <Link2 className="w-4 h-4 text-slate-400 shrink-0" />
              <input
                data-testid="listing-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") onImport(); }}
                placeholder="https://www.zillow.com/homedetails/..."
                className="flex-1 bg-transparent outline-none text-slate-900 placeholder:text-slate-400 text-sm px-3 py-4"
              />
            </div>
            <button
              data-testid="generate-my-ads-btn"
              onClick={onImport}
              className="inline-flex items-center justify-center gap-2 bg-[#1D6AFF] hover:bg-[#1552C6] text-white font-semibold rounded-2xl px-6 py-4 transition-all shadow-[0_16px_36px_-10px_rgba(29,106,255,0.5)] hover:-translate-y-0.5 whitespace-nowrap"
            >
              Build My Campaign <Sparkles className="w-4 h-4" />
            </button>
          </div>

          {importError && (
            <div data-testid="import-error" className="mt-4 inline-flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5 text-left max-w-xl">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{importError}</span>
            </div>
          )}

          <div className="mt-6">
            <button data-testid="manual-entry-toggle" onClick={onManual}
              className="text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors inline-flex items-center gap-1.5">
              <Pencil className="w-3.5 h-3.5" /> No link? Enter property details manually
            </button>
          </div>

          <p className="mt-6 text-xs text-slate-400 max-w-md mx-auto leading-relaxed">
            Works best with public listing pages. Some portals block automatic import — you can always enter details and upload photos yourself.
          </p>
        </div>
      </div>

      <LibrarySection library={library} activeIds={activeIds} onDeleteLib={onDeleteLib} onDownloadLib={onDownloadLib} onReopen={onReopen} onUseLib={onUseLib} />
    </div>
  );
}

// ─────────────────────────── LIBRARY SECTION ─────────────────────────────
function LibrarySection({ library, activeIds = [], onDeleteLib, onDownloadLib, onReopen, onUseLib }) {
  if (!library || library.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <FolderOpen className="w-4 h-4 text-slate-500" />
        <h2 className="font-display text-lg font-bold text-slate-900">Your listings</h2>
        <span className="text-xs text-slate-500">({library.length})</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="creative-library">
        {library.map((it) => {
          const active = activeIds.includes(it.id);
          return (
          <div key={it.id} data-testid={`library-item-${it.id}`} className="group relative rounded-xl overflow-hidden border border-slate-200 bg-white shadow-sm flex flex-col">
            <div className="relative aspect-square bg-slate-100">
              {it.thumb && <img src={it.thumb} alt={it.name} className="w-full h-full object-cover" />}
              <span className={`absolute top-1.5 left-1.5 text-[9px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${active ? "bg-emerald-500 text-white" : "bg-white/90 text-slate-600 border border-slate-200"}`}>
                {active ? "Active" : "Ready"}
              </span>
              <button data-testid={`library-delete-${it.id}`} onClick={() => onDeleteLib(it.id)} title="Delete" className="absolute top-1.5 right-1.5 w-7 h-7 rounded-lg bg-black/55 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="p-2.5 flex-1 flex flex-col">
              <div className="text-xs font-semibold text-slate-900 truncate">{it.name}</div>
              <div className="text-[10px] text-slate-500 mb-2">{it.template}</div>
              <div className="mt-auto grid grid-cols-3 gap-1">
                <button data-testid={`library-reopen-${it.id}`} onClick={() => onReopen(it.id)} title="Reopen & edit" className="inline-flex items-center justify-center gap-1 text-[10px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg py-1.5 transition-colors">
                  <Pencil className="w-3 h-3" /> Edit
                </button>
                <button data-testid={`library-download-${it.id}`} onClick={() => onDownloadLib(it.id)} title="Download PNG" className="inline-flex items-center justify-center text-[10px] font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg py-1.5 transition-colors">
                  <Download className="w-3 h-3" />
                </button>
                <button data-testid={`library-use-${it.id}`} onClick={() => onUseLib(it.id)} title="Promote with Lead Engine" className="inline-flex items-center justify-center text-[10px] font-bold text-white bg-[#1D6AFF] hover:bg-[#1552C6] rounded-lg py-1.5 transition-colors">
                  <Rocket className="w-3 h-3" />
                </button>
              </div>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────── WORKING STAGE ───────────────────────────────
const WORK_STEPS = [
  { icon: Home, label: "Importing property" },
  { icon: ImageIcon, label: "Analyzing & ranking photos" },
  { icon: Wand2, label: "Writing campaign copy" },
  { icon: Sparkles, label: "Building your 4 creatives" },
];
function WorkingStage({ step }) {
  return (
    <div className="bg-white border border-slate-200 rounded-3xl p-10 sm:p-16 shadow-sm" data-testid="working-stage">
      <div className="max-w-md mx-auto">
        <div className="flex items-center justify-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-[#1D6AFF]/10 border border-[#1D6AFF]/20 flex items-center justify-center">
            <Loader2 className="w-7 h-7 text-[#1D6AFF] animate-spin" />
          </div>
        </div>
        <div className="space-y-3">
          {WORK_STEPS.map((s, i) => {
            const done = i < step; const active = i === step;
            return (
              <div key={s.label} className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                done ? "bg-emerald-50 border-emerald-200" : active ? "bg-[#1D6AFF]/5 border-[#1D6AFF]/25" : "bg-slate-50 border-slate-200 opacity-60"}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${done ? "bg-emerald-500 text-white" : active ? "bg-[#1D6AFF] text-white" : "bg-white text-slate-400 border border-slate-200"}`}>
                  {done ? <CheckCircle2 className="w-4 h-4" /> : active ? <Loader2 className="w-4 h-4 animate-spin" /> : <s.icon className="w-4 h-4" />}
                </div>
                <span className={`text-sm font-semibold ${done ? "text-emerald-700" : active ? "text-slate-900" : "text-slate-500"}`}>{s.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── VERIFY STAGE ────────────────────────────────
function VerifyStage({ property, setField, photos, selected, toggleSelected, removePhoto, onUploadClick, rights, setRights, genError, onBack, onGenerate }) {
  const [feat, setFeat] = useState("");
  const addFeature = () => {
    const v = feat.trim();
    if (v && !property.features.includes(v)) setField("features", [...property.features, v].slice(0, 8));
    setFeat("");
  };
  return (
    <div className="space-y-6" data-testid="verify-stage">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Start over
      </button>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <span className="w-1 h-5 rounded-full bg-[#1D6AFF]" />
          <h3 className="font-display text-lg font-bold text-slate-900">Verify the details</h3>
        </div>
        <p className="text-xs text-slate-500 mb-5 ml-3">Confirm everything is accurate — the AI only uses what's here. Blank fields are simply left out.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <VField label="Address" value={property.address} onChange={(v) => setField("address", v)} testId="v-address" placeholder="2389 Stradella Rd" />
          <VField label="City" value={property.city} onChange={(v) => setField("city", v)} testId="v-city" placeholder="Bel Air, CA" />
          <VField label="Price" value={property.price} onChange={(v) => setField("price", v)} testId="v-price" placeholder="$4,250,000" />
          <VField label="Status" value={property.status} onChange={(v) => setField("status", v)} testId="v-status" placeholder="For Sale / Just Listed" />
          <div className="grid grid-cols-3 gap-3">
            <VField label="Beds" value={property.beds} onChange={(v) => setField("beds", v)} testId="v-beds" placeholder="5" />
            <VField label="Baths" value={property.baths} onChange={(v) => setField("baths", v)} testId="v-baths" placeholder="6" />
            <VField label="Sqft" value={property.sqft} onChange={(v) => setField("sqft", v)} testId="v-sqft" placeholder="5,200" />
          </div>
          <VField label="Property type" value={property.property_type} onChange={(v) => setField("property_type", v)} testId="v-type" placeholder="House" />
        </div>
        <div className="mt-4">
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">Description</label>
          <textarea data-testid="v-description" value={property.description} onChange={(e) => setField("description", e.target.value)} rows={2}
            placeholder="Short description from the listing"
            className="w-full bg-slate-50 border border-slate-200 focus:border-[#1D6AFF] focus:bg-white outline-none text-slate-900 placeholder:text-slate-400 rounded-xl px-3.5 py-2.5 text-sm transition-all resize-none" />
        </div>
        <div className="mt-4">
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Verified features</label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {property.features.map((f) => (
              <span key={f} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-[#1D6AFF]/8 text-[#1552C6] border border-[#1D6AFF]/20 rounded-full px-2.5 py-1">
                {f}
                <button onClick={() => setField("features", property.features.filter((x) => x !== f))}><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input data-testid="v-feature-input" value={feat} onChange={(e) => setFeat(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(); } }}
              placeholder="Add a feature (e.g. Ocean view)"
              className="flex-1 bg-slate-50 border border-slate-200 focus:border-[#1D6AFF] focus:bg-white outline-none text-slate-900 placeholder:text-slate-400 rounded-xl px-3.5 py-2.5 text-sm transition-all" />
            <button data-testid="v-feature-add" onClick={addFeature} className="px-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold">Add</button>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="w-1 h-5 rounded-full bg-[#1D6AFF]" />
            <h3 className="font-display text-lg font-bold text-slate-900">Photos</h3>
            <span className="text-xs text-slate-500">Pick your best ({selected.length} selected)</span>
          </div>
          <button data-testid="verify-upload-photos" onClick={onUploadClick} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl px-3.5 py-2">
            <Upload className="w-3.5 h-3.5" /> Add photos
          </button>
        </div>
        {photos.length === 0 ? (
          <div className="text-sm text-slate-500 bg-slate-50 border border-dashed border-slate-300 rounded-xl p-6 text-center">No photos yet. Upload at least one to build your campaign.</div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="verify-photo-grid">
            {photos.map((p, i) => {
              const on = selected.includes(i);
              return (
                <div key={i} className={`relative rounded-xl overflow-hidden aspect-square border-2 transition-all cursor-pointer ${on ? "border-[#1D6AFF] ring-2 ring-[#1D6AFF]/20" : "border-slate-200 opacity-70 hover:opacity-100"}`}
                     data-testid={`verify-photo-${i}`} onClick={() => toggleSelected(i)}>
                  <img src={p} alt="" className="w-full h-full object-cover" />
                  {on && <div className="absolute top-1.5 left-1.5 w-6 h-6 rounded-full bg-[#1D6AFF] text-white flex items-center justify-center"><CheckCircle2 className="w-4 h-4" /></div>}
                  <button onClick={(e) => { e.stopPropagation(); removePhoto(i); }} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-red-500"><X className="w-3.5 h-3.5" /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <label className="flex items-start gap-3 cursor-pointer mb-5" data-testid="rights-confirm">
          <input type="checkbox" checked={rights} onChange={(e) => setRights(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#1D6AFF]" />
          <span className="text-sm text-slate-600 leading-relaxed">
            <ShieldCheck className="w-4 h-4 text-emerald-600 inline mr-1 -mt-0.5" />
            I confirm I have the rights to advertise this listing and use these photos, and all details above are accurate.
          </span>
        </label>

        {genError && (
          <div className="mb-4 inline-flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {genError}
          </div>
        )}

        <button data-testid="generate-ads-btn" onClick={onGenerate}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#1D6AFF] hover:bg-[#1552C6] text-white font-bold rounded-xl px-8 py-3.5 transition-all shadow-[0_16px_36px_-10px_rgba(29,106,255,0.5)] hover:-translate-y-0.5">
          <Wand2 className="w-4 h-4" /> Generate Campaign Kit <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

const VField = ({ label, value, onChange, placeholder, testId }) => (
  <div>
    <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">{label}</label>
    <input data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full bg-slate-50 border border-slate-200 focus:border-[#1D6AFF] focus:bg-white outline-none text-slate-900 placeholder:text-slate-400 rounded-xl px-3.5 py-2.5 text-sm transition-all" />
  </div>
);

// ─────────────────────── CAMPAIGN KIT (RESULTS) ──────────────────────────
function CampaignKitStage({
  kit, photos, property, buildAssetOpts, assetDepKey,
  onSwapHero, onSetAsset, onRegenerate, onToggleCarouselPhoto,
  updateHeadline, updatePrimary, updateCta, onDownloadAsset,
  isElite, sellerEnabled, setSellerEnabled,
  onBack, onReset, onPreview, onPromote, onSave,
}) {
  const [promoting, setPromoting] = useState(false);
  return (
    <div className="space-y-6" data-testid="campaign-kit-stage">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" /> Back to details
        </button>
        <button data-testid="new-listing-btn" onClick={onReset} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
          <RefreshCw className="w-3 h-3" /> New listing
        </button>
      </div>

      {/* Ready banner */}
      <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-[#1D6AFF]/[0.10] via-[#EAF2FF] to-white p-6 sm:p-8 shadow-[0_30px_80px_-50px_rgba(29,106,255,0.45)]" data-testid="campaign-ready-banner">
        <div className="absolute -top-24 -right-16 w-80 h-80 bg-[#1D6AFF]/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 left-1/4 w-64 h-64 bg-sky-400/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 mb-4">
            <CheckCircle2 className="w-3 h-3" /> Campaign Built
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-5">Your Listing Campaign is Ready</h1>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mb-7">
            <ReadyItem label="4 creatives ready" />
            <ReadyItem label="Meta / Instagram ready" />
            <ReadyItem label="Google campaign assets ready" />
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button data-testid="preview-campaign-btn" onClick={onPreview}
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-800 font-semibold rounded-xl px-6 py-3.5 transition-all shadow-sm">
              <Eye className="w-4 h-4" /> Preview Campaign
            </button>
            <button data-testid="promote-lead-engine-btn" disabled={promoting}
              onClick={async () => { setPromoting(true); try { await onPromote(); } finally { setPromoting(false); } }}
              className="inline-flex items-center justify-center gap-2 bg-[#1D6AFF] hover:bg-[#1552C6] disabled:opacity-60 text-white font-bold rounded-xl px-6 py-3.5 transition-all shadow-[0_16px_36px_-10px_rgba(29,106,255,0.6)] hover:-translate-y-0.5">
              {promoting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Rocket className="w-4 h-4" /> Promote with Lead Engine</>}
            </button>
            <button data-testid="save-kit-btn" onClick={onSave}
              className="inline-flex items-center justify-center gap-2 text-slate-500 hover:text-slate-800 font-semibold rounded-xl px-4 py-3.5 transition-all">
              <Save className="w-4 h-4" /> Save for later
            </button>
          </div>
        </div>
      </div>

      {/* Asset grid */}
      <div className="grid sm:grid-cols-2 gap-5" data-testid="kit-assets-grid">
        {ASSET_ORDER.map((key) => (
          <AssetCard
            key={key} assetKey={key} def={ASSET_DEFS[key]}
            kit={kit} photos={photos}
            depKey={assetDepKey(key)} buildSrc={() => renderAd(buildAssetOpts(key))}
            onRegenerate={() => onRegenerate(key)}
            onDownload={() => onDownloadAsset(key)}
            onPickPhoto={(idx) => (key === "hero" ? onSwapHero(idx) : key === "carousel" ? onToggleCarouselPhoto(idx) : onSetAsset(key, { photoIndex: idx }))}
          />
        ))}
      </div>

      {/* Copy editor */}
      <CopyPanel
        kit={kit} updateHeadline={updateHeadline} updatePrimary={updatePrimary} updateCta={updateCta}
        isElite={isElite} sellerEnabled={sellerEnabled} setSellerEnabled={setSellerEnabled}
      />
    </div>
  );
}

const ReadyItem = ({ label }) => (
  <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
    <span className="w-5 h-5 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center"><CheckCircle2 className="w-3 h-3 text-emerald-600" /></span>
    {label}
  </span>
);

function AssetCard({ assetKey, def, kit, photos, depKey, buildSrc, onRegenerate, onDownload, onPickPhoto }) {
  const [src, setSrc] = useState(null);
  const [rendering, setRendering] = useState(true);
  const [showSwap, setShowSwap] = useState(false);
  const a = kit.assets[assetKey];
  const Icon = def.Icon;

  useEffect(() => {
    let alive = true; setRendering(true);
    buildSrc().then((d) => { if (alive) { setSrc(d); setRendering(false); } }).catch(() => { if (alive) setRendering(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depKey]);

  const aspect = def.size === "1080x1920" ? "aspect-[9/16]" : def.size === "1080x1350" ? "aspect-[4/5]" : "aspect-square";
  const selectedIdx = assetKey === "carousel" ? a.photoIndices : [a.photoIndex];

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm flex flex-col" data-testid={`kit-asset-${assetKey}`}>
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-7 h-7 rounded-lg bg-[#1D6AFF]/10 flex items-center justify-center shrink-0"><Icon className="w-3.5 h-3.5 text-[#1D6AFF]" /></span>
          <div className="min-w-0">
            <div className="text-sm font-bold text-slate-900 truncate">{def.label}</div>
            <div className="text-[10px] text-slate-500 truncate">{def.sub}{assetKey === "carousel" ? ` · ${a.photoIndices.length} photos` : ""}</div>
          </div>
        </div>
        <button data-testid={`kit-regen-${assetKey}`} onClick={onRegenerate} title="Regenerate" className="text-xs font-semibold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 shrink-0">
          <RefreshCw className="w-3.5 h-3.5" /> Regenerate
        </button>
      </div>

      <div className="relative bg-slate-900 flex items-center justify-center" style={{ minHeight: 160 }}>
        <div className={`${aspect} w-full max-h-[460px]`}>
          {rendering && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="w-6 h-6 text-slate-400 animate-spin" /></div>}
          {src && <img data-testid={`kit-preview-${assetKey}`} src={src} alt={def.label} className="w-full h-full object-contain" />}
        </div>
      </div>

      <div className="p-3 flex flex-col gap-2">
        <div className="grid grid-cols-2 gap-2">
          <button data-testid={`kit-swap-${assetKey}`} onClick={() => setShowSwap((v) => !v)} className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl py-2.5 transition-colors">
            <ImageIcon className="w-3.5 h-3.5" /> {assetKey === "hero" ? "Change hero" : assetKey === "carousel" ? "Edit photos" : "Swap photo"}
          </button>
          <button data-testid={`kit-download-${assetKey}`} onClick={onDownload} className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl py-2.5 transition-colors">
            <Download className="w-3.5 h-3.5" /> Download
          </button>
        </div>
        {showSwap && (
          <div className="flex items-center gap-1.5 flex-wrap pt-1" data-testid={`kit-photostrip-${assetKey}`}>
            {photos.map((p, pi) => {
              const on = selectedIdx.includes(pi);
              return (
                <button key={pi} data-testid={`kit-photo-${assetKey}-${pi}`} onClick={() => onPickPhoto(pi)}
                  className={`w-10 h-10 rounded-md overflow-hidden border-2 transition-all ${on ? "border-[#1D6AFF]" : "border-transparent opacity-55 hover:opacity-100"}`}>
                  <img src={p} alt="" className="w-full h-full object-cover" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function CopyPanel({ kit, updateHeadline, updatePrimary, updateCta, isElite, sellerEnabled, setSellerEnabled }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="kit-copy-panel">
      <div className="flex items-center gap-2 mb-5">
        <span className="w-1 h-5 rounded-full bg-[#1D6AFF]" />
        <h3 className="font-display text-lg font-bold text-slate-900">Campaign copy</h3>
        <span className="text-xs text-slate-500">The Lead Engine tests these automatically</span>
      </div>

      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Headlines (3)</label>
      <div className="space-y-3 mb-5">
        {[0, 1, 2].map((i) => {
          const val = kit.headlines[i] || "";
          const over = val.length > HEADLINE_MAX;
          return (
            <div key={i}>
              <div className="relative">
                <input data-testid={`kit-headline-${i}`} value={val} onChange={(e) => updateHeadline(i, e.target.value)}
                  className={`w-full bg-slate-50 border ${over ? "border-amber-400 focus:border-amber-500" : "border-slate-200 focus:border-[#1D6AFF]"} focus:bg-white outline-none text-slate-900 rounded-xl px-3.5 py-2.5 pr-16 text-sm transition-all`} />
                <span data-testid={`kit-headline-count-${i}`} className={`absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-semibold tabular-nums ${over ? "text-amber-600" : "text-slate-400"}`}>{val.length}/{HEADLINE_MAX}</span>
              </div>
              {over && (
                <p data-testid={`kit-headline-warning-${i}`} className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-700">
                  <AlertTriangle className="w-3 h-3 shrink-0" /> This headline may get cut off — keep it under {HEADLINE_MAX} characters for a clean fit.
                </p>
              )}
            </div>
          );
        })}
      </div>

      <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Primary text (2)</label>
      <div className="space-y-3 mb-5">
        {[0, 1].map((i) => {
          const val = kit.primaryTexts[i] || "";
          const over = val.length > PRIMARY_MAX;
          return (
            <div key={i}>
              <div className="relative">
                <textarea data-testid={`kit-primary-${i}`} value={val} onChange={(e) => updatePrimary(i, e.target.value)} rows={2}
                  className={`w-full bg-slate-50 border ${over ? "border-amber-400 focus:border-amber-500" : "border-slate-200 focus:border-[#1D6AFF]"} focus:bg-white outline-none text-slate-900 rounded-xl px-3.5 py-2.5 pb-6 text-sm transition-all resize-none`} />
                <span data-testid={`kit-primary-count-${i}`} className={`absolute right-3 bottom-2 text-[11px] font-semibold tabular-nums ${over ? "text-amber-600" : "text-slate-400"}`}>{val.length}/{PRIMARY_MAX}</span>
              </div>
              {over && (
                <p data-testid={`kit-primary-warning-${i}`} className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-700">
                  <AlertTriangle className="w-3 h-3 shrink-0" /> This may be truncated with "…See more" in the feed — keep it under {PRIMARY_MAX} characters.
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="max-w-xs">
        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Call to action</label>
        {(() => {
          const val = kit.cta || "";
          const over = val.length > CTA_MAX;
          return (
            <>
              <div className="relative">
                <input data-testid="kit-cta" value={val} onChange={(e) => updateCta(e.target.value)}
                  className={`w-full bg-slate-50 border ${over ? "border-amber-400 focus:border-amber-500" : "border-slate-200 focus:border-[#1D6AFF]"} focus:bg-white outline-none text-slate-900 rounded-xl px-3.5 py-2.5 pr-14 text-sm transition-all`} />
                <span data-testid="kit-cta-count" className={`absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-semibold tabular-nums ${over ? "text-amber-600" : "text-slate-400"}`}>{val.length}/{CTA_MAX}</span>
              </div>
              {over && (
                <p data-testid="kit-cta-warning" className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-700">
                  <AlertTriangle className="w-3 h-3 shrink-0" /> Keep the button short — under {CTA_MAX} characters reads cleanest.
                </p>
              )}
            </>
          );
        })()}
      </div>

      {/* Elite seller angle */}
      <div className={`mt-6 rounded-2xl border p-4 ${sellerEnabled ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`} data-testid="seller-angle-box">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="w-9 h-9 rounded-xl bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0"><Megaphone className="w-4 h-4 text-amber-600" /></span>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-slate-900">Use this listing to attract sellers</span>
                {!isElite && <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-amber-100 text-amber-700 border border-amber-200 rounded-full px-1.5 py-0.5 uppercase"><Crown className="w-2.5 h-2.5" />Elite</span>}
              </div>
              <p className="text-xs text-slate-500 mt-0.5 max-w-md leading-relaxed">Adds a seller-intent angle that feeds the Seller side of your Lead Engine — e.g. "Another home just listed nearby. Curious what yours could sell for?"</p>
            </div>
          </div>
          <button data-testid="seller-angle-toggle" disabled={!isElite}
            onClick={() => setSellerEnabled((v) => !v)}
            className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${sellerEnabled ? "bg-amber-500" : "bg-slate-300"} ${!isElite ? "opacity-50 cursor-not-allowed" : ""}`}>
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${sellerEnabled ? "translate-x-5" : ""}`} />
          </button>
        </div>
        {sellerEnabled && isElite && kit.seller && (
          <div className="mt-3 pt-3 border-t border-amber-200 space-y-1.5" data-testid="seller-angle-preview">
            {(kit.seller.primary_texts || []).slice(0, 2).map((t, i) => (
              <p key={i} className="text-xs text-amber-900 leading-relaxed">“{t}”</p>
            ))}
          </div>
        )}
        {sellerEnabled && isElite && !kit.seller && (
          <p className="mt-3 pt-3 border-t border-amber-200 text-xs text-amber-800">Seller copy will be generated on your next regenerate. Toggle this on before generating to include it automatically.</p>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────── PREVIEW MODAL ───────────────────────────────
function PreviewModal({ kit, photos, buildAssetOpts, isElite, sellerEnabled, onClose }) {
  const [srcs, setSrcs] = useState({});
  const [carSlides, setCarSlides] = useState([]);
  const [carIdx, setCarIdx] = useState(0);
  const carRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const out = {};
      for (const key of ASSET_ORDER) {
        try { out[key] = await renderAd(buildAssetOpts(key)); } catch (_e) { /* skip */ }
      }
      if (alive) setSrcs(out);
      const idxs = kit.assets.carousel.photoIndices || [];
      const slides = [];
      for (const pi of idxs) {
        try { slides.push(await renderAd(buildAssetOpts("carousel", pi))); } catch (_e) { /* skip */ }
      }
      if (alive) setCarSlides(slides);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToSlide = (i) => {
    const el = carRef.current; if (!el) return;
    const n = Math.max(0, Math.min(carSlides.length - 1, i));
    el.scrollTo({ left: n * el.clientWidth, behavior: "smooth" });
    setCarIdx(n);
  };
  const onCarScroll = () => {
    const el = carRef.current; if (!el) return;
    setCarIdx(Math.round(el.scrollLeft / Math.max(1, el.clientWidth)));
  };

  return (
    <div data-testid="preview-campaign-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-4xl max-h-[88vh] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-slate-200">
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-slate-100 px-6 py-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-slate-900">Campaign Preview</h3>
          <button data-testid="preview-close" onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6">
          {/* Swipeable carousel viewer */}
          {carSlides.length > 0 && (
            <div className="mb-7" data-testid="preview-carousel">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> Carousel · slide {carIdx + 1} of {carSlides.length}</div>
                <div className="hidden sm:flex items-center gap-2">
                  <button data-testid="preview-carousel-prev" onClick={() => scrollToSlide(carIdx - 1)} disabled={carIdx <= 0}
                    className="w-8 h-8 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
                  <button data-testid="preview-carousel-next" onClick={() => scrollToSlide(carIdx + 1)} disabled={carIdx >= carSlides.length - 1}
                    className="w-8 h-8 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-colors"><ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
              <div ref={carRef} onScroll={onCarScroll} style={{ scrollbarWidth: "none" }}
                className="flex overflow-x-auto snap-x snap-mandatory rounded-xl bg-slate-900 [&::-webkit-scrollbar]:hidden">
                {carSlides.map((src, i) => (
                  <div key={i} className="min-w-full snap-center flex items-center justify-center" style={{ minHeight: 260 }}>
                    <img data-testid={`preview-carousel-slide-${i}`} src={src} alt={`Carousel slide ${i + 1}`} className="max-h-[54vh] w-auto object-contain" />
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-1.5 mt-3">
                {carSlides.map((_, i) => (
                  <button key={i} data-testid={`preview-carousel-dot-${i}`} onClick={() => scrollToSlide(i)}
                    className={`h-1.5 rounded-full transition-all ${i === carIdx ? "w-5 bg-[#1D6AFF]" : "w-1.5 bg-slate-300 hover:bg-slate-400"}`} />
                ))}
              </div>
              <p className="text-[11px] text-slate-400 text-center mt-2 sm:hidden">Swipe to see all slides</p>
            </div>
          )}

          {/* 4-up overview */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {ASSET_ORDER.map((key) => (
              <div key={key} className="text-center">
                <div className="rounded-xl overflow-hidden bg-slate-900 mb-2 flex items-center justify-center" style={{ minHeight: 120 }}>
                  {srcs[key] ? <img src={srcs[key]} alt={ASSET_DEFS[key].label} className="w-full h-full object-contain" /> : <Loader2 className="w-5 h-5 text-slate-500 animate-spin my-10" />}
                </div>
                <div className="text-[11px] font-semibold text-slate-700">{ASSET_DEFS[key].label}{key === "carousel" ? ` · ${carSlides.length || (kit.assets.carousel.photoIndices || []).length}` : ""}</div>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-slate-200 p-4">
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Headlines</div>
            <ul className="list-disc ml-5 mb-4 space-y-1 text-sm text-slate-800">{kit.headlines.map((h, i) => <li key={i}>{h}</li>)}</ul>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Primary text</div>
            <ul className="space-y-1.5 mb-4 text-sm text-slate-600">{kit.primaryTexts.map((t, i) => <li key={i} className="leading-relaxed">{t}</li>)}</ul>
            <div className="text-sm"><span className="font-bold text-slate-700">CTA:</span> <span className="text-slate-600">{kit.cta}</span></div>
            {sellerEnabled && isElite && kit.seller && (
              <div className="mt-4 pt-4 border-t border-amber-200">
                <div className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-2 inline-flex items-center gap-1"><Megaphone className="w-3 h-3" /> Seller angle</div>
                <ul className="space-y-1.5 text-sm text-amber-900">{(kit.seller.primary_texts || []).map((t, i) => <li key={i} className="leading-relaxed">“{t}”</li>)}</ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── LIMIT MODAL ─────────────────────────────────
function LimitModal({ cap, onClose, onReplace, onIncrease }) {
  return (
    <div data-testid="listing-limit-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" />
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
        <button data-testid="listing-limit-close" onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"><X className="w-4 h-4" /></button>
        <div className="p-6 sm:p-7">
          <div className="w-12 h-12 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center mb-4"><AlertTriangle className="w-6 h-6 text-amber-500" /></div>
          <h3 className="font-display text-xl font-bold text-slate-900 mb-2">You've reached your active listing limit</h3>
          <p data-testid="listing-limit-message" className="text-sm text-slate-600 leading-relaxed mb-6">
            Your current marketing budget supports up to <b className="text-slate-900">{cap} active listing{cap === 1 ? "" : "s"}</b>. Your campaign is saved and marked Ready.
          </p>
          <div className="space-y-2.5">
            <button data-testid="listing-limit-replace" onClick={onReplace}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-slate-800 font-semibold px-5 py-3 text-sm hover:bg-slate-50 transition-colors">
              <Repeat className="w-4 h-4" /> Replace an active listing
            </button>
            <button data-testid="listing-limit-increase-budget" onClick={onIncrease}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl text-white font-semibold px-5 py-3 text-sm bg-gradient-to-r from-[#1D6AFF] to-[#2E7DFF] shadow-[0_14px_30px_-12px_rgba(29,106,255,0.6)] hover:-translate-y-0.5 transition-all">
              <TrendingUp className="w-4 h-4" /> Increase marketing budget
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
